import download from 'downloadjs';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { v4 as uuid } from 'uuid';
import watermark from 'watermarkjs';
import file from '@/utils/file';
import { getVersion } from '../util';

type PreparedImage = {
  imageUrl: string;
  changed: boolean;
};

export const imageUrlToBlob = (url: string): Promise<Blob> =>
  new Promise((resolve) => {
    fetch(url, {
      headers: {},
    })
      .then((response) => {
        resolve(response.blob());
      })
      .catch(() => {
        resolve(null);
      });
  });

// If width is a string, it will be treated as a percentage of the target width
const resizeWatermark = ({
  width,
  center,
  opacity = 1,
}: {
  width: number | string;
  center?: boolean;
  opacity?: number;
}) => {
  return (target: any, w: any) => {
    const context = target.getContext('2d');
    context.save();

    const targetWidth = target.width;
    const targetHeight = target.height;

    // Convert percentage width to actual width
    const parsedWidth = typeof width === 'string' ? (parseFloat(width) / 100) * targetWidth : width;

    const widthRatio = w.width / parsedWidth;
    const height = w.height / widthRatio;

    // Set the opacity (global alpha) of the context
    context.globalAlpha = opacity;
    if (center) {
      context.drawImage(
        w,
        (targetWidth - parsedWidth) / 2,
        (targetHeight - height) / 2,
        parsedWidth,
        height,
      );
    } else {
      context.drawImage(w, 10, 10, parsedWidth, height);
    }

    context.restore();
    return target;
  };
};

export const prepareImageForDownload = async ({ url, tour }): Promise<PreparedImage> => {
  const { client } = tour;
  let imageUrl = `${url}${getVersion(new Date())}`;
  let changed = false;

  if (!tour.unbranded) {
    if (client?.logo) {
      const logoUrl = `${process.env.NEXT_PUBLIC_CDN}/clients/${client.id}/logo.png${getVersion(
        new Date(),
      )}`;

      if (await file.existFile(logoUrl)) {
        imageUrl = await watermark([imageUrl, logoUrl], {
          init(c: any) {
            c.crossOrigin = 'anonymous';
          },
        }).blob(
          resizeWatermark({
            width: client?.widthLogo ? 250 : 150,
          }),
        );
        changed = true;
      }
    }

    if (client?.watermark) {
      const watermarkUrl = `${process.env.NEXT_PUBLIC_CDN}/clients/${
        tour.client?.id
      }/watermark.png${getVersion(new Date())}`;
      if (await file.existFile(watermarkUrl)) {
        imageUrl = await watermark([imageUrl, watermarkUrl], {
          init(c: any) {
            c.crossOrigin = 'anonymous';
          },
        }).blob(
          resizeWatermark({
            width: '30', // %
            opacity: 0.5,
            center: true,
          }),
        );
        changed = true;
      }
    }
  }

  return {
    imageUrl,
    changed,
  };
};

export const downloadImage = async (url: string, tour: any) => {
  const { imageUrl, changed } = await prepareImageForDownload({ url, tour });
  const blob = await imageUrlToBlob(imageUrl);
  download(changed ? imageUrl : blob, `${uuid()}.png`, 'image/png');
};

export const downloadAllImages = async (images: any, tour: any) => {
  const preparedImages: PreparedImage[] = await Promise.all(
    images.map((i) => prepareImageForDownload({ url: i, tour })),
  );
  const zip = new JSZip();
  const galleryArchive = zip.folder('gallery');

  await Promise.all(
    (preparedImages || []).map(async ({ imageUrl, changed }) => {
      const imageBlob = !changed ? await imageUrlToBlob(imageUrl) : imageUrl;
      if (imageBlob) {
        galleryArchive.file(`${uuid()}.jpg`, imageBlob, { base64: true });
      }
    }),
  );

  return zip
    .generateAsync({ type: 'blob' })
    .then((data) => {
      saveAs(data, 'gallery.zip');
      return true;
    })
    .catch(() => false);
};

export default {
  downloadAllImages,
};
